/**
 * ⭐ `AW20` · **Lo que se manda**: texto libre, los botones que el brazo rutea de
 * verdad, y los dos escapes que no se borran.
 *
 * ⛔⛔ **LOS BOTONES QUE RUTEAN SON TRES, POR BRAZO, Y SALEN DE CONFIGURACIÓN.**
 * `contracts/channel/index.ts` los declara `[CONFIG]` —`demoStartButtonId` (`:1994`),
 * `demoHandoffButtonId` (`:2033`), `optOutButtonId` (`:2206`)— y es textual sobre el
 * resto: *«sólo los selectores configurados […] y CUALQUIER otro botón cae por
 * `interactivo-sin-ruteo`»*. ⇒ **dibujar un cuarto sería dibujar algo que no hace
 * nada**, y hornear un literal acá andaría en el brazo de quien lo escribió y caería
 * en `interactivo-sin-ruteo` en el otro **sin que nada falle**: el operador vería el
 * rojo correcto por el motivo equivocado.
 *
 * ⛔ **NO HAY PLANTILLA QUE LEER, Y LA SALIENTE NO LLEVA BOTONES.** `Entrega`
 * (`apps/bot-web/src/entregas.ts`) tiene siete campos y **ninguno es botones**;
 * `buttonId` existe **sólo del lado entrante**. ⇒ la pantalla **no puede** saber qué
 * botones ofreció una saliente concreta, y **no lo finge**: ofrece los que el brazo
 * tiene configurados, no los de un mensaje.
 *
 * ⭐⭐ RASTRO · `AW57` · 16/9/2026 — **«SIETE CAMPOS» QUEDÓ VIEJO: HOY SON OCHO.**
 * `[medido 16/9/2026]` `AP46` sumó `ms_desde_el_entrante` a `Entrega`. El párrafo
 * de arriba **no se borra** (`ADR-0082`: redactar no es borrar) porque su argumento
 * sigue entero: el octavo campo tampoco es botones, así que la saliente sigue sin
 * llevarlos y esta pantalla sigue sin poder afirmar cuáles ofreció. Lo único que
 * cambia es el número. ⚠️ Ningún `grep` del campo nuevo encuentra este renglón, y
 * `tsc` no lee prosa: si no se tocaba acá, no lo tocaba nadie.
 *
 * ⭐⭐ RASTRO DE LA VUELTA · 16/9/2026, EL MISMO DÍA — **VOLVIÓ A SER SIETE.**
 * `AP46` y `AW57` se retiraron del árbol: el dueño se quedó con la implementación de
 * `origin/master`, que mide OTRO intervalo desde otra fuente (`ms_transcurridos`,
 * por `GET /turnos`). `ADR-0082`: tampoco se borra el RASTRO de arriba — lo que dejó
 * de ser cierto es su *««SIETE CAMPOS» QUEDÓ VIEJO: HOY SON OCHO»*. El párrafo
 * original vuelve a ser literal y el número hizo el recorrido **7 → 8 → 7**. El
 * argumento nunca se movió: ninguno de esos campos es botones, ni el octavo cuando
 * entró ni ahora que se fue.
 *
 * ⛔⛔ **NINGÚN BOTÓN DIBUJA UNA CONFIRMACIÓN QUE LA BASE NO ESCRIBIÓ.**
 * `ADR-0040 §②`: *«se puede fingir lo que no promete nada que el sistema no vaya a
 * cumplir. NO SE PUEDE FINGIR UNA OBLIGACIÓN.»* Apretar el de derivación **no**
 * dibuja «te derivamos»: dibuja el desenlace que el motor devolvió, que hoy es
 * `interactivo-sin-ruteo` porque `CH8` está diferido. **La pantalla no lo suaviza.**
 *
 * ⚠️ **Y el modo demo no se enciende, no se pide y no se asume.** Si el entorno lo
 * tiene encendido, acá se ve `interactivo-demo` **crudo**, que es la distinción que
 * `ADR-0040 §⑤` pide.
 *
 * ⭐ **LOS DOS ESCAPES NO SE BORRAN.** El `<input>` de identificador de botón es el
 * único camino para producir `interactivo-sin-ruteo` a pedido, y el de tipo no-texto
 * el único para producir `modalidad`. Los dos son desenlaces legítimos del motor y
 * de los ocho que esta pantalla existe para mostrar: sacarlos **reduciría el
 * instrumento** mientras lo hace más cómodo.
 */

import { useState } from "react"
import type { JSX } from "react"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { TIPO_INTERACTIVO, TIPO_TEXTO } from "./borde-simulador.ts"

/** Un botón que el brazo rutea, tal como la instancia lo declaró. */
interface BotonRuteable {
  readonly campo: string
  /**
   * ⭐⭐ `AW43` · **LO QUE SE LEE EN EL BOTÓN. Corto a propósito.**
   *
   * ⛔ Antes decía «Botón de inicio». **La palabra «Botón» es ruido**: el elemento ya
   * es un `<button>`, así que un lector de pantalla anunciaba *«Botón de inicio,
   * botón»*. Sacarla **mejora** lo que se oye y, de paso, es lo que hace entrar los
   * tres en UN renglón.
   */
  readonly rotulo: string
  /**
   * ⭐⭐ `AW43` · **LO QUE SE ESCRIBE EN EL HILO. Sigue siendo el largo.**
   *
   * ⛔ **No se acortó, y es deliberado**: en la burbuja no hay un `<button>` al lado
   * que le dé contexto — «Baja» suelto en un hilo no dice qué pasó. Son dos textos
   * distintos porque tienen dos lectores distintos.
   *
   * ⛔⛔ **Y NO SE LLAMA `resumen`, AUNQUE ES LO QUE VIAJA COMO `resumen`. `AW45`.**
   * `resumen` es el ANCLA POR FORMA con la que `AW36 · J9` encuentra «la línea visible
   * del simulador» para exigirle que declare el campo de tipo. Llamar `resumen` a un
   * campo de un tipo que habla de BOTONES —y que no tiene por qué llevar `tipo`—
   * **le da al juez una segunda forma que no es la que busca**, y lo pone rojo sin que
   * nada de la línea visible haya cambiado. Medido: `LineaVisible` nunca dejó de
   * declarar `resumen` NI `tipo`; el rojo lo produjo este nombre.
   */
  readonly textoDelHilo: string
  readonly id: string
}

/**
 * Los botones que ESTA instancia tiene configurados. **Ausente ⇒ no se dibuja**, ni
 * deshabilitado ni con un valor de relleno: es la misma regla que `botWebUrl`.
 */
export function botonesRuteables(config: ConfiguracionInstancia): readonly BotonRuteable[] {
  /*
   * ⭐⭐ `AW43` · **DOS TEXTOS POR BOTÓN: el corto se ve, el largo se escribe.**
   *
   * ⛔⛔ Medido en Chrome real el 31/8/2026: `.sim__botonera` ocupaba **72 px de los
   * 183 del redactor** porque los tres rótulos largos **envolvían en dos renglones**,
   * y el redactor se llevaba el 28 % del panel contra 389 px del hilo. El dueño lo
   * reportó como *«el scroll está siendo ocultado por los botones»* — medí que **no
   * hay superposición** (`SE_SUPERPONEN: false`, 0 px, en los tres regímenes del
   * `clamp`), así que el hilo no estaba tapado: **estaba apretado.**
   *
   * ⛔ **Los tres SE QUEDAN.** Son el único camino para producir `interactivo-*` a
   * pedido, que es la razón de ser de esta pantalla. Lo que se achica es el rótulo.
   */
  const candidatos: readonly (readonly [string, string, string, string | undefined])[] = [
    ["botonInicio", "Inicio", "Botón de inicio", config.botonInicio],
    ["botonDerivacion", "Derivación a humano", "Botón de derivación a humano", config.botonDerivacion],
    ["botonBaja", "Baja", "Botón de baja", config.botonBaja],
  ]
  return candidatos
    .filter((c): c is readonly [string, string, string, string] => c[3] !== undefined)
    .map(([campo, rotulo, textoDelHilo, id]) => ({ campo, rotulo, textoDelHilo, id }))
}

export function Redactor({
  config,
  puedeMandar,
  onMandar,
}: {
  readonly config: ConfiguracionInstancia
  readonly puedeMandar: boolean
  readonly onMandar: (
    resumen: string,
    tipo: string,
    contenido: string | null,
    interactivo: { readonly buttonId: string; readonly text: string } | null,
  ) => void
}): JSX.Element {
  const [texto, setTexto] = useState("")
  const [botonId, setBotonId] = useState("")
  /**
   * ⭐⭐ `R2-UI9` · **EL TEXTO VISIBLE DEL BOTÓN A MANO, Y POR QUÉ ES UN SEGUNDO
   * CAMPO Y NO EL MISMO.**
   *
   * Desde `R2-BW2` el cuerpo lleva `buttonId` **y** `text`, y son dos datos
   * distintos: el id rutea los tres botónes de `[CONFIG]`, el texto visible es con
   * el que se busca la respuesta automática del brazo (`RT-2`).
   *
   * ⛔ **Copiar el id en `text` habría apagado un desenlace.** El escape existe
   * para *«producir `interactivo-sin-ruteo` a pedido»* y el encabezado de este
   * archivo dice que sacarlo **reduce el instrumento**. Con `text` = id, `bot-web`
   * armaría un `interactive` válido y el desenlace pasaría a ser
   * `boton-sin-respuesta`: el escape dejaría de poder producir el suyo, y encima
   * el nuevo sería el diagnóstico falso que `RT-2` describe.
   *
   * ⭐ **Vacío ⇒ falla cerrado.** `bot-web` exige `text` no vacío; con `""` el
   * `interactive` queda `null` y el toque cae en `interactivo-sin-ruteo`. ⇒ **los
   * DOS desenlaces del botón se pueden pedir desde acá**: sin texto el de ruteo,
   * con texto el de la tabla de respuestas del brazo.
   */
  const [textoDelBoton, setTextoDelBoton] = useState("")
  const [tipoNoTexto, setTipoNoTexto] = useState("")

  const botones = botonesRuteables(config)

  return (
    <section className="sim__redactor" aria-label="Qué se manda">
      {/*
        ⛔⭐⭐ RASTRO · `AW36` · 28/8/2026 — «TEXTO LIBRE» DEJA DE VIAJAR COMO RESUMEN.

        `[medido 28/8/2026]` El `<label>` de acá abajo está OCULTO (`sim__oculto`), así
        que el «Texto libre» grande que se veía en el hilo **no era este rótulo**: era
        el primer argumento de `onMandar` —el `resumen`—, escrito **dos veces** en este
        archivo, y la burbuja lo pinta con `.simulador__contenido`, o sea a tamaño de
        cuerpo y en el lugar del contenido.

        ⇒ **el arreglo no es achicar la letra: es dejar de mandar un rótulo por el
        canal del contenido.** El resumen de un entrante de texto pasa a ser **el
        texto**, que es lo que de verdad se mandó; que era de tipo texto lo dice ahora
        la marca de tipo de `hilo.tsx`, que es canal de rótulo.

        ⚠️ El `<label>` oculto se queda como está: es el nombre accesible del campo y
        nunca fue el problema.

        ⚠️ Los otros tres gestos de este archivo **no tienen texto que mostrar** —un
        botón ruteable, un botón a mano, un tipo cualquiera— así que su resumen sigue
        siendo una descripción. Un resumen vacío dejaría burbujas mudas.
      */}
      <div className="sim__renglon">
        <label className="sim__oculto" htmlFor="simulador-texto">
          Texto libre
        </label>
        <input
          id="simulador-texto"
          className="sim__entrada"
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || !puedeMandar || texto.trim() === "") return
            const contenido = texto
            setTexto("")
            // ⭐⭐ `AW36` · acá decía `"Texto libre"`. Ver el bloque de arriba.
            onMandar(contenido, TIPO_TEXTO, contenido, null)
          }}
        />
        <button
          type="button"
          className="boton boton--primario"
          /*
           * ⛔⛔ El `disabled` depende de `enviando` y de que haya conversación
           * armada — NUNCA del estado de la ventana. El motor NO bloquea al vencer:
           * abre una sesión nueva. Ver `sesiones.ts` §①.
           */
          disabled={!puedeMandar || texto.trim() === ""}
          onClick={() => {
            const contenido = texto
            setTexto("")
            // ⭐⭐ `AW36` · la SEGUNDA copia del mismo literal. Ver el bloque de arriba.
            onMandar(contenido, TIPO_TEXTO, contenido, null)
          }}
        >
          Enviar texto
        </button>
      </div>

      {botones.length === 0 ? (
        <p className="sim__ayuda">
          Esta instancia no declaró ningún identificador de botón, así que no se dibuja ninguno. ⛔
          No es un defecto de la pantalla: los rutea el brazo y se cargan por configuración.
        </p>
      ) : (
        <div className="sim__botonera">
          {botones.map((b) => (
            <button
              key={b.campo}
              type="button"
              className="sim__boton-ruteable"
              data-boton={b.campo}
              disabled={!puedeMandar}
              /*
               * ⛔ El id sale de `config`, nunca de un literal acá: un literal anda en
               * el brazo de quien lo escribió y cae en `interactivo-sin-ruteo` en el
               * otro, y ese síntoma es indistinguible del hueco real de `CH8`.
               */
              /* ⭐ `AW43` · Al hilo va el texto LARGO, no el rótulo corto. */
              /*
               * ⭐⭐⭐ `R2-UI9` · **`text` ES EL RÓTULO QUE EL OPERADOR VE Y APRETA**
               * (`b.rotulo`), y no el id ni el texto del hilo.
               *
               * ⛔ **No es el id**: `RT-2` dice textual que el identificador de un
               * botón de Meta no coincide con nada de lo que trae la sincronización,
               * así que usar `b.id` como etiqueta haría que la búsqueda de la
               * respuesta automática fallara y el desenlace fuera
               * `boton-sin-respuesta` sobre un botón que SÍ tiene respuesta — un
               * diagnóstico falso. `ADR-0338` lo mide.
               *
               * ⛔ **Y no es `b.textoDelHilo`**: ese es el texto LARGO que `AW43`
               * escribió para la burbuja —«Botón de inicio»— y el propio `AW43` dice
               * cuál de los dos se VE: *«el corto se ve, el largo se escribe»*. Lo
               * que el participante tendría delante en la plataforma de mensajería es
               * la etiqueta del botón, y acá eso es el rótulo.
               *
               * ⚠️ **El id SIGUE saliendo de `config`**: el argumento de arriba no se
               * toca. Son dos datos distintos viajando juntos, que es exactamente lo
               * que `InboundInteractive` declara.
               */
              onClick={() =>
                onMandar(b.textoDelHilo, TIPO_INTERACTIVO, null, {
                  buttonId: b.id,
                  text: b.rotulo,
                })
              }
            >
              {b.rotulo}
            </button>
          ))}
        </div>
      )}

      <details className="sim__escape">
        <summary>Escapes: botón a mano y entrante que no es texto</summary>

        <div className="sim__renglon">
          <label className="sim__oculto" htmlFor="simulador-boton">
            Identificador de botón a mano (escape)
          </label>
          <input
            id="simulador-boton"
            className="sim__entrada"
            type="text"
            value={botonId}
            onChange={(e) => setBotonId(e.target.value)}
          />
          {/*
            ⭐ `R2-UI9` · el segundo campo del escape. **Opcional a propósito**: ver
            ⟦textoDelBoton⟧ arriba. Sin él, el escape sigue produciendo
            `interactivo-sin-ruteo`; con él, el toque atraviesa el MISMO camino que
            el sobre real de Meta y el desenlace lo decide la tabla del brazo.
          */}
          <label className="sim__oculto" htmlFor="simulador-boton-texto">
            Texto visible del botón (opcional)
          </label>
          <input
            id="simulador-boton-texto"
            className="sim__entrada"
            type="text"
            placeholder="Texto visible del botón (opcional)"
            value={textoDelBoton}
            onChange={(e) => setTextoDelBoton(e.target.value)}
          />
          <button
            type="button"
            className="boton"
            disabled={!puedeMandar || botonId.trim() === ""}
            onClick={() => {
              const id = botonId
              const etiqueta = textoDelBoton
              setBotonId("")
              setTextoDelBoton("")
              onMandar(
                etiqueta.trim() === "" ? `Botón «${id}»` : `Botón «${etiqueta}»`,
                TIPO_INTERACTIVO,
                null,
                { buttonId: id, text: etiqueta },
              )
            }}
          >
            Enviar botón
          </button>
        </div>
        <p className="sim__ayuda">
          El único camino para producir <code>interactivo-sin-ruteo</code> a pedido. No se saca.
          ⚠️ Es lo que pasa cuando el <strong>texto visible</strong> queda vacío: el borde no sabe a
          qué botón rutear. Con texto, el toque busca la respuesta automática del brazo y el
          desenlace es <code>boton-respondido</code> o <code>boton-sin-respuesta</code>.
        </p>

        <div className="sim__renglon">
          <label className="sim__oculto" htmlFor="simulador-tipo">
            Tipo de entrante que no es texto (escape)
          </label>
          <input
            id="simulador-tipo"
            className="sim__entrada"
            type="text"
            value={tipoNoTexto}
            onChange={(e) => setTipoNoTexto(e.target.value)}
          />
          <button
            type="button"
            className="boton"
            disabled={!puedeMandar || tipoNoTexto.trim() === ""}
            onClick={() => {
              const t = tipoNoTexto
              setTipoNoTexto("")
              onMandar(`Entrante de tipo «${t}»`, t, null, null)
            }}
          >
            Enviar entrante que no es texto
          </button>
        </div>
        <p className="sim__ayuda">
          El único camino para producir <code>modalidad</code> a pedido. El módulo define ese
          desenlace por la NEGATIVA —«ni texto ni interactivo»—, así que no hay lista que ofrecer.
        </p>
      </details>
    </section>
  )
}
